import { FASTAPI_BASE_URL } from "../../config";

const BASE_URL = `${FASTAPI_BASE_URL}/ai-exhibit`;
const OUTBOX_KEY = "ai_exhibit_v2_analytics_outbox_v1";

function canUseStorage() {
  return typeof localStorage !== "undefined";
}

function newId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const hex = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx";
  return hex.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function readOutbox() {
  try {
    if (!canUseStorage()) return [];
    const parsed = JSON.parse(localStorage.getItem(OUTBOX_KEY) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeOutbox(items) {
  try {
    if (!canUseStorage()) return;
    localStorage.setItem(OUTBOX_KEY, JSON.stringify(items || []));
  } catch {}
}

class AnalyticsHttpError extends Error {
  constructor(message, status) {
    super(message);
    this.name = "AnalyticsHttpError";
    this.status = status;
    this.retryable =
      status === 408 ||
      status === 429 ||
      status >= 500 ||
      (status === 404 && /session/i.test(String(message || "")));
  }
}

async function parseResponse(response) {
  const text = await response.text();
  let data = {};
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { detail: text };
    }
  }
  if (!response.ok) {
    throw new AnalyticsHttpError(
      data?.detail || data?.message || `HTTP ${response.status}`,
      response.status
    );
  }
  return data;
}

async function send(method, path, body) {
  const response = await fetch(`${BASE_URL}${path}`, {
    method,
    headers: { "Content-Type": "application/json" },
    body: body == null ? undefined : JSON.stringify(body),
  });
  return parseResponse(response);
}

function queueWrite(method, path, body) {
  const items = readOutbox();
  items.push({ method, path, body, queuedAt: new Date().toISOString() });
  writeOutbox(items);
}

async function writeWithOutbox(method, path, body) {
  try {
    return await send(method, path, body);
  } catch (error) {
    // 4xx validation/identity problems are permanent until code/config is fixed.
    // Do not poison the retry outbox with them. Network errors and transient
    // server errors are safe to retry because write IDs are idempotent.
    const shouldQueue = !(error instanceof AnalyticsHttpError) || error.retryable;
    if (shouldQueue) {
      queueWrite(method, path, body);
      console.warn(
        "[AI EXPLAINED ANALYTICS] queued for retry:",
        path,
        error?.message || error
      );
      return { ok: false, queued: true, error: error?.message || String(error) };
    }

    console.error(
      "[AI EXPLAINED ANALYTICS] permanent write failure:",
      path,
      error?.status,
      error?.message || error
    );
    return {
      ok: false,
      queued: false,
      status: error.status,
      error: error?.message || String(error),
    };
  }
}

export async function flushExhibitAnalyticsOutbox() {
  const items = readOutbox();
  if (!items.length) return { flushed: 0, dropped: 0, remaining: 0 };

  const remaining = [];
  let flushed = 0;
  let dropped = 0;

  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    try {
      await send(item.method, item.path, item.body);
      flushed += 1;
    } catch (error) {
      if (error instanceof AnalyticsHttpError && !error.retryable) {
        // Bad identity/payload will never be fixed by retrying the same event.
        dropped += 1;
        console.error(
          "[AI EXPLAINED ANALYTICS] dropping non-retryable queued write:",
          item.path,
          error.status,
          error.message
        );
        continue;
      }

      // Preserve order from the first transient failure onward because later
      // writes may depend on an earlier session-start record.
      remaining.push(...items.slice(i));
      break;
    }
  }

  writeOutbox(remaining);
  return { flushed, dropped, remaining: remaining.length };
}

export function createExhibitAnalyticsId() {
  return newId();
}

export function startExhibitSession(payload) {
  return writeWithOutbox("POST", "/sessions", payload);
}

export function updateExhibitSession(sessionId, payload) {
  return writeWithOutbox("PATCH", `/sessions/${encodeURIComponent(sessionId)}`, payload);
}

export function endExhibitSession(sessionId, payload) {
  return writeWithOutbox("POST", `/sessions/${encodeURIComponent(sessionId)}/end`, payload);
}

export function logExhibitEvent(payload) {
  return writeWithOutbox("POST", "/events", {
    id: payload.id || newId(),
    ...payload,
  });
}

export function logExhibitAttempt(payload) {
  return writeWithOutbox("POST", "/attempts", {
    id: payload.id || newId(),
    ...payload,
  });
}

export async function getExhibitAttemptStats({ stationId, itemId = null, contentVersion = null }) {
  const params = new URLSearchParams();
  params.set("station_id", stationId);
  if (itemId) params.set("item_id", itemId);
  if (contentVersion) params.set("content_version", contentVersion);

  const response = await fetch(`${BASE_URL}/attempts/stats?${params.toString()}`);
  return parseResponse(response);
}

export async function getExhibitSessionStats({ days = 7, studentEmail = null, kioskId = null } = {}) {
  const params = new URLSearchParams();
  params.set("days", String(days));
  if (studentEmail) params.set("student_email", studentEmail);
  if (kioskId) params.set("kiosk_id", kioskId);

  const response = await fetch(`${BASE_URL}/sessions/stats?${params.toString()}`);
  return parseResponse(response);
}

export const AI_EXHIBIT_ANALYTICS_OUTBOX_KEY = OUTBOX_KEY;
